import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { FaArrowsLeftRight } from "react-icons/fa6";
import {
  FiArrowRight,
  FiBarChart2,
  FiChevronUp,
  FiTarget,
  FiTrendingDown,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";

import { twMerge } from "tailwind-merge";

import {
  faCaretDown,
  faCaretUp,
  faFileAlt,
  faFilePdf,
  faPaperclip,
  faPlus,
  faSpinner,
  faTimes,
  faTimesCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import ConfirmModal from "components/ConfirmModal";
import { LoaderSingleLine } from "components/customElements";
import DatePicker from "components/DatePicker";
import { entityCompactUrl } from "components/EntityCard";
import EntityLogo from "components/EntityLogo";
import FileInput from "components/FileInput";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import ProfilePicture from "components/ProfilePicture";
import SearchInput from "components/SearchInput";
import SwitchButton from "components/SwitchButton";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";
import { fileToDataURL } from "utils/fileToDataURL";
import shortSecurity from "utils/shortSecurity";

export default function DiscussionEditorV3({
  editor,
  suggestions,
  onSetTaggedEntity,
  taggedEntity,
  setSentiment,
  sentiment,
  attachments,
  setAttachments,
  canSubmit,
  onCancel,
  submit,
  isSubmitting,
  parent,
  inputCommentable,
  isAnonymous,
  toggleAnonymous,
  mode,
  setMode,
  targetPrice,
  setTargetPrice,
  targetDate,
  setTargetDate,
  setPollQuestion,
  pollOptions,
  setPollOptions,
  pollAllowMultipleSelections,
  setPollAllowMultipleSelections,
  pollClosesAt,
  setPollClosesAt,
  isEditing,
  onlyAttachments,
  setEntityPrice,
}: {
  editor: any;
  suggestions: React.ReactNode;
  onSetTaggedEntity: (_entity: any) => void;
  taggedEntity: any;
  sentiment: "bullish" | "bearish" | "neutral" | null;
  setSentiment: (_sentiment: "bullish" | "bearish" | "neutral" | null) => void;
  attachments: any[];
  setAttachments: (_attachments: any[]) => void;
  canSubmit: boolean;
  onCancel?: () => void;
  submit: () => void;
  isSubmitting: boolean;
  parent?: any;
  inputCommentable?: any;
  isAnonymous: boolean;
  toggleAnonymous: () => void;
  mode: null | "polling" | "prediction";
  setMode: (_mode: null | "polling" | "prediction") => void;
  targetPrice: any;
  setTargetPrice: (_targetPrice: any) => void;
  targetDate: any;
  setTargetDate: (_targetDate: any) => void;
  pollQuestion: string | null;
  setPollQuestion: (_question: string | null) => void;
  pollOptions: string[];
  setPollOptions: (_options: string[]) => void;
  pollAllowMultipleSelections: boolean;
  setPollAllowMultipleSelections: (_value: boolean) => void;
  pollClosesAt: string | null;
  setPollClosesAt: (_date: string | null) => void;
  isEditing?: boolean;
  onlyAttachments?: boolean;
  setEntityPrice?: (_price: any) => void;
}) {
  const { mobile } = useMediaQuery();
  const entity = useMemo(() => {
    return (
      taggedEntity ||
      (!["insights", "insight"].includes(
        parent?.commentable?.type?.toLowerCase()
      )
        ? parent?.commentable
        : parent?.commentable?.primaryEntity ||
          parent?.commentable?.["primary-entity"]) ||
      parent?.commentableEntities?.[0]
    );
  }, [parent?.commentable, parent?.commentableEntities, taggedEntity]);

  const url = entityCompactUrl(entity?.security);
  const { setNotification } = useNotification();
  const { data: entityPrice, isValidating } = useApi(url, baseFetcher, {
    onSuccess: (data: any) => {
      setEntityPrice?.(data);
    },
  });
  const { account } = useAuthentication();

  const commentable = useMemo(() => {
    if (parent) return null;
    if (entity) {
      const prettyName =
        entity.prettyName || entity["pretty-name"] || entity["display-name"];
      const security =
        entity.shortSecurity || shortSecurity(entity["security"]);

      const allowEdit =
        (!inputCommentable && !parent) ||
        ["insights", "insight"].includes(inputCommentable?.type?.toLowerCase());

      return (
        <div
          className={twMerge(
            "flex border border-solid border-[#ddd] rounded-[10px] overflow-hidden"
          )}
        >
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex items-center">
              <div className="mr-[10px]">
                <EntityLogo
                  entity={{
                    ...entity,
                    logoUrl: entity.logoUrl || entity["logo-url"],
                  }}
                  size={40}
                  withAltText={false}
                />
              </div>
              <div
                className={twMerge(
                  "flex-1 flex items-center justify-between",
                  !allowEdit && "pr-[10px]"
                )}
              >
                <span className="line-clamp-1 flex-1 text-[14px]">
                  {prettyName} ({security})
                </span>
                {isValidating ? (
                  <div className="flex items-center animate-pulse">
                    <div className="w-[10px] h-[10px] bg-nearly-white rounded-full"></div>
                    <span className="text-[12px] text-shady-gray ml-[5px]">
                      Loading...
                    </span>
                  </div>
                ) : entityPrice?.price ? (
                  <div className="flex flex-col md:flex-row items-end md:items-center">
                    <span className="text-[12px]">
                      {entityPrice?.price} {entityPrice?.currency}
                    </span>
                    <span
                      className={twMerge(
                        "text-[12px]",
                        entityPrice?.percentage > 0
                          ? "text-bullish-green"
                          : "text-bearish-red"
                      )}
                    >
                      <FontAwesomeIcon
                        icon={
                          entityPrice?.percentage > 0 ? faCaretUp : faCaretDown
                        }
                      />
                      {entityPrice?.percentage}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {allowEdit && (
            <div
              className="w-[40px] group bg-nearly-white hover:bg-bearish-red cursor-pointer flex items-center justify-center ml-[10px] transition-all"
              onClick={() => onSetTaggedEntity(null)}
            >
              <FontAwesomeIcon
                icon={faTimesCircle}
                className="text-[15px] text-bearish-red group-hover:text-white"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <SearchInput
        inputClassName={() =>
          twMerge(
            "!p-0 !py-[20px] border border-solid rounded-[10px] border-[#ddd]"
          )
        }
        icon
        className={twMerge(
          taggedEntity ? "w-auto" : "w-full",
          "md:w-auto min-w-[300px]"
        )}
        histories={
          ["insights", "insight"].includes(
            inputCommentable?.type?.toLowerCase()
          )
            ? [
                ...(inputCommentable.primaryEntity
                  ? [inputCommentable.primaryEntity]
                  : []),
                ...(inputCommentable.secondaryEntities || []),
              ]
            : []
        }
        iconClassName={() => "text-shady-gray"}
        customHistoriesHeader={
          <div className="bg-[#eee] text-[15px] px-[7px] py-[3px]">
            Suggested Entities
          </div>
        }
        placeholder={"Tag an entity"}
        resultPlaceholder="Search by name or Bloomberg Ticker"
        endpoint={"/api/v2/entities"}
        queryParam="filter[fulltext]"
        onItemSelected={(entity) => {
          onSetTaggedEntity(entity);
        }}
        searchListClassName="max-h-[200px] overflow-y-scroll"
      />
    );
  }, [
    entity,
    entityPrice?.currency,
    entityPrice?.percentage,
    entityPrice?.price,
    inputCommentable,
    isValidating,
    onSetTaggedEntity,
    parent,
    taggedEntity,
  ]);

  const sentimentInput = useMemo(() => {
    if (!!mode) return null;

    return (
      <div className="flex flex-col gap-[8px]">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-bold text-subtle-black">
            Your sentiment
          </span>
          <span className="text-[12px] font-semibold text-bearish-red">
            Required
          </span>
        </div>
        <div className="flex gap-[8px]">
          <button
            className={twMerge(
              "flex-1 flex items-center justify-center gap-[6px] px-[12px] py-[10px]",
              "border border-solid rounded-[5px] transition-all bg-white cursor-pointer font-semibold text-[13px]",
              sentiment === "bullish"
                ? "border-bullish-green bg-bullish-green/5 text-bullish-green"
                : "border-[#ddd] text-shady-gray hover:text-bullish-green hover:border-bullish-green/40 hover:bg-bullish-green/5"
            )}
            onClick={() =>
              setSentiment(sentiment === "bullish" ? null : "bullish")
            }
          >
            <FiTrendingUp size={15} />
            Bullish
          </button>
          <button
            className={twMerge(
              "flex-1 flex items-center justify-center gap-[6px] px-[12px] py-[10px]",
              "border border-solid rounded-[5px] transition-all bg-white cursor-pointer font-semibold text-[13px]",
              sentiment === "neutral"
                ? "border-subtle-black bg-subtle-black/5 text-subtle-black"
                : "border-[#ddd] text-shady-gray hover:border-subtle-black/30 hover:bg-subtle-black/5"
            )}
            onClick={() =>
              setSentiment(sentiment === "neutral" ? null : "neutral")
            }
          >
            <FaArrowsLeftRight size={15} />
            Neutral
          </button>
          <button
            className={twMerge(
              "flex-1 flex items-center justify-center gap-[6px] px-[12px] py-[10px]",
              "border border-solid rounded-[5px] transition-all bg-white cursor-pointer font-semibold text-[13px]",
              sentiment === "bearish"
                ? "border-bearish-red bg-bearish-red/5 text-bearish-red"
                : "border-[#ddd] text-shady-gray hover:text-bearish-red hover:border-bearish-red/40 hover:bg-bearish-red/5"
            )}
            onClick={() =>
              setSentiment(sentiment === "bearish" ? null : "bearish")
            }
          >
            <FiTrendingDown size={15} />
            Bearish
          </button>
        </div>
      </div>
    );
  }, [mode, sentiment, setSentiment]);

  const onRemoveAttachment = useCallback(
    (index: number) => {
      const newAttachments = attachments.filter((_, i) => i !== index);
      setAttachments(newAttachments);
    },
    [attachments, setAttachments]
  );

  const attachmentsPreview = useMemo(() => {
    return attachments.map((attachment, index) => {
      const name =
        attachment.name ||
        attachment.sourceFileName ||
        attachment["source-file-name"];
      const fileExtension = name?.split(".").pop();

      if (fileExtension === "pdf") {
        return (
          <div
            key={name}
            className="flex flex-col justify-center items-center gap-[10px] group/attachment cursor-pointer"
            onClick={() => onRemoveAttachment(index)}
          >
            <div className="w-[50px] relative h-[50px] md:w-[100px] md:h-[100px] bg-smart-teal/20 flex items-center justify-center rounded-[5px]">
              <FontAwesomeIcon
                icon={faFilePdf}
                className="text-white text-[24px] md:text-[48px]"
              />
              <div className="absolute top-0 right-0 left-0 bottom-0 hidden group-hover/attachment:flex bg-red-500/20 items-center justify-center rounded-[5px]">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-red-500 text-[24px]"
                />
              </div>
            </div>
            <span className="max-w-[50px] md:max-w-[100px] flex flex-col items-center text-[12px] text-center">
              <span className="line-clamp-1 max-w-[50px] md:max-w-[100px]">
                {name}
              </span>
              <span>({fileExtension})</span>
            </span>
          </div>
        );
      } else if (
        fileExtension === "png" ||
        fileExtension === "jpg" ||
        fileExtension === "jpeg"
      ) {
        return (
          <div
            key={name}
            className="flex flex-col justify-center items-center gap-[10px] group/attachment cursor-pointer"
            onClick={() => onRemoveAttachment(index)}
          >
            <div className="relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={attachment.source}
                alt={name}
                className="w-[50px] h-[50px] md:w-[100px] md:h-[100px] border-1 border-solid border-nearly-white rounded-[5px] object-cover"
              />
              <div className="absolute top-0 right-0 left-0 bottom-0 hidden group-hover/attachment:flex bg-red-500/20 items-center justify-center rounded-[5px]">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-red-500 text-[24px]"
                />
              </div>
            </div>
            <span className="max-w-[50px] md:max-w-[100px] flex flex-col items-center text-[12px] text-center">
              <span className="line-clamp-1 max-w-[50px] md:max-w-[100px]">
                {name}
              </span>
              <span>({fileExtension})</span>
            </span>
          </div>
        );
      } else {
        return (
          <div
            key={name}
            className="flex flex-col justify-center items-center gap-[10px] group/attachment cursor-pointer"
            onClick={() => onRemoveAttachment(index)}
          >
            <div className="relative w-[50px] h-[50px] md:w-[100px] md:h-[100px] bg-smart-teal/20 flex items-center justify-center rounded-[5px]">
              <FontAwesomeIcon
                icon={faFileAlt}
                className="text-white text-[24px] md:text-[48px]"
              />
              <div className="absolute top-0 right-0 left-0 bottom-0 hidden group-hover/attachment:flex bg-red-500/20 items-center justify-center rounded-[5px]">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-red-500 text-[24px]"
                />
              </div>
            </div>
            <span className="max-w-[50px] md:max-w-[100px] flex flex-col items-center text-[12px] text-center">
              <span className="line-clamp-1 max-w-[50px] md:max-w-[100px]">
                {name}
              </span>
              <span>({fileExtension})</span>
            </span>
          </div>
        );
      }
    });
  }, [attachments, onRemoveAttachment]);

  const onFileChange = useCallback(
    async (data) => {
      const files = data as File[];
      const invalidFiles = files.filter(
        (attachment) => attachment.size > 5 * 1024 * 1024
      );

      const formatted = await Promise.all(
        files
          .filter((attachment) => attachment.size < 5 * 1024 * 1024)
          .map(async (attachment) => {
            return {
              name: attachment.name,
              source: await fileToDataURL(attachment),
            };
          })
      );

      if ([...attachments, ...formatted].length > 4) {
        setNotification(`Maximum 4 files can be attached`, "error");
        return;
      }

      if (invalidFiles.length > 0) {
        setNotification(
          `${invalidFiles.length} file(s) was skipped because it exceeds the 5 MB size limit`,
          "error"
        );
      }

      setAttachments([...attachments, ...formatted]);
    },
    [attachments, setAttachments, setNotification]
  );

  const [isDragOver, setIsDragOver] = useState(false);
  const [isAddToPostExpanded, setIsAddToPostExpanded] = useState(true);
  const [attachmentRemovalConfirm, setAttachmentRemovalConfirm] = useState<
    "polling" | "prediction" | null
  >(null);

  useEffect(() => {
    setIsAddToPostExpanded(!mode);
  }, [mode]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setIsDragOver(false);
      if (!mode) onFileChange(acceptedFiles);
    },
    onDragEnter: () => !mode && setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
    onDropRejected: () => setIsDragOver(false),
    noClick: true,
    noKeyboard: true,
    multiple: true,
    maxSize: 5 * 1024 * 1024,
    disabled: !!mode,
  });

  const attachmentsInput = useMemo(() => {
    const cardContent = (
      <div
        className={twMerge(
          "flex-1 md:flex-[1.1] flex flex-col gap-[4px] p-[12px] text-left",
          "border border-solid rounded-[10px] transition-all",
          mode
            ? "border-[#ddd] opacity-40 cursor-not-allowed"
            : "border-[#ddd] cursor-pointer hover:border-smart-teal/40 hover:bg-smart-teal/5"
        )}
      >
        <FileInput
          onChange={onFileChange}
          multiple
          extensions={[
            ".pdf",
            ".xls",
            ".xlsx",
            ".doc",
            ".docx",
            ".csv",
            ".png",
            ".jpg",
            ".jpeg",
          ]}
          max={4}
          disabled={!!mode}
          containerClassName="m-[-12px] p-[12px]"
        >
          <div className="flex items-center gap-[10px]">
            <div className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] bg-smart-teal/10 flex-shrink-0">
              <FontAwesomeIcon
                icon={faPaperclip}
                className="text-smart-teal text-[14px]"
              />
            </div>
            <div className="flex flex-col gap-[2px]">
              <span className="font-semibold text-[14px]">Attach</span>
              <span className="text-[12px] text-shady-gray">
                Images, Files · 5 MB max · Up to 4
              </span>
            </div>
          </div>
        </FileInput>
      </div>
    );

    return cardContent;
  }, [mode, onFileChange]);

  const polling = useMemo(() => {
    return (
      <>
        <div className="flex items-center justify-end">
          <div
            className="flex items-center gap-[10px] text-sm text-inactive-gray hover:text-bearish-red cursor-pointer justify-end mb-[10px]"
            onClick={() => {
              setMode(null);
              setSentiment(null);
              setPollQuestion(null);
              setPollOptions([]);
              setPollAllowMultipleSelections(false);
              setPollClosesAt(null);
            }}
          >
            Remove
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className="mb-[10px] flex flex-col gap-[10px]">
          <div className="flex flex-col gap-[8px] mt-[2px]">
            {pollOptions.map((option, index) => (
              <div key={index} className="flex items-center gap-[8px]">
                <div
                  className={twMerge(
                    "flex-1 flex items-center gap-[8px]",
                    "border border-solid border-black/20 rounded-[5px]",
                    "px-[10px] py-[6px]"
                  )}
                >
                  <span className="text-[12px] text-shady-gray font-medium min-w-[18px]">
                    {index + 1}.
                  </span>
                  <input
                    className="flex-1 !border-0 !outline-none !shadow-none focus:!outline-none !ring-0 !p-0 text-[14px] bg-transparent"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    maxLength={80}
                    onChange={(e) => {
                      const next = [...pollOptions];
                      next[index] = e.target.value;
                      setPollOptions(next);
                    }}
                  />
                  <span
                    className={twMerge(
                      "text-[11px] flex-shrink-0 tabular-nums",
                      option.length >= 70
                        ? "text-bearish-red font-semibold"
                        : "text-inactive-gray"
                    )}
                  >
                    {option.length}/80
                  </span>
                </div>
                {pollOptions.length > 2 && (
                  <button
                    className={twMerge(
                      "flex items-center justify-center w-[28px] h-[28px] rounded-full flex-shrink-0",
                      "border border-solid border-nearly-white bg-nearly-white",
                      "hover:border-bearish-red hover:bg-bearish-red/10",
                      "text-inactive-gray hover:text-bearish-red transition-all"
                    )}
                    onClick={() =>
                      setPollOptions(pollOptions.filter((_, i) => i !== index))
                    }
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
                  </button>
                )}
              </div>
            ))}
            {pollOptions.length < 4 && (
              <button
                className={twMerge(
                  "flex items-center gap-[6px] w-fit mt-[2px]",
                  "border border-solid border-smart-teal",
                  "bg-white hover:bg-smart-teal",
                  "text-smart-teal hover:text-white",
                  "text-[13px] font-medium rounded-full px-[12px] py-[5px]",
                  "transition-all"
                )}
                onClick={() => setPollOptions([...pollOptions, ""])}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add option
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-[10px] border-0 border-t border-solid border-[#eee] pt-[15px] mt-[15px]">
            <div className="flex items-center gap-[8px]">
              <span className="text-[13px]">Allow multiple selections</span>
              <SwitchButton
                checked={pollAllowMultipleSelections}
                onClick={() =>
                  setPollAllowMultipleSelections(!pollAllowMultipleSelections)
                }
              />
            </div>
            <div className="flex flex-wrap items-center gap-[8px]">
              <span className="text-[13px]">Closes at</span>
              <DatePicker
                value={
                  pollClosesAt
                    ? new Date(pollClosesAt + "T00:00:00")
                    : undefined
                }
                removeButton={
                  <button className="p-0 outline-none border-0 bg-transparent text-bearish-red hover:text-[#85273a] mt-[5px]">
                    <FontAwesomeIcon
                      icon={faTimesCircle}
                      className="text-[20px]"
                    />
                  </button>
                }
                containerClassName="flex flex-row items-center gap-[10px]"
                inputClassName="text-[14px] rounded-[5px]"
                minDate={new Date()}
                onSelect={(date) => {
                  if (!date) {
                    setPollClosesAt(null);
                    return;
                  }
                  const y = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, "0");
                  const d = String(date.getDate()).padStart(2, "0");
                  setPollClosesAt(`${y}-${m}-${d}`);
                }}
              />
              <div className="flex items-center gap-[6px]">
                <span className="text-[11px] font-bold text-shady-gray uppercase mr-[4px]">
                  Presets:
                </span>
                {[1, 3, 7, 13].map((d) => {
                  const presetDate = new Date();
                  presetDate.setDate(presetDate.getDate() + d);
                  const presetValue = `${presetDate.getFullYear()}-${String(
                    presetDate.getMonth() + 1
                  ).padStart(2, "0")}-${String(presetDate.getDate()).padStart(
                    2,
                    "0"
                  )}`;
                  return (
                    <button
                      key={d}
                      type="button"
                      className={twMerge(
                        "px-[10px] py-[4px] rounded-full text-[12px] font-medium transition-all",
                        "border border-solid",
                        pollClosesAt === presetValue
                          ? "border-smart-teal bg-smart-teal/10 text-smart-teal"
                          : "border-nearly-white bg-white hover:bg-smart-teal/10 hover:border-smart-teal/30 text-shady-gray hover:text-smart-teal"
                      )}
                      onClick={() => setPollClosesAt(presetValue)}
                    >
                      {d}D
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }, [
    pollAllowMultipleSelections,
    pollClosesAt,
    pollOptions,
    setMode,
    setPollAllowMultipleSelections,
    setPollClosesAt,
    setPollOptions,
    setPollQuestion,
    setSentiment,
  ]);

  const prediction = useMemo(() => {
    const upside =
      targetPrice && entityPrice?.price
        ? ((Number(targetPrice) - Number(entityPrice?.price)) /
            Number(entityPrice?.price)) *
          100
        : null;

    const toLocalDateString = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const setTargetDateFromPreset = (months: number) => {
      const date = new Date();
      date.setMonth(date.getMonth() + months);
      setTargetDate(toLocalDateString(date));
    };

    const adjustPrice = (percent: number) => {
      if (entityPrice?.price) {
        const current = Number(targetPrice || entityPrice?.price);
        const next = current * (1 + percent / 100);
        setTargetPrice(next.toFixed(2));
        if (next > Number(entityPrice?.price)) {
          setSentiment("bullish");
        } else if (next < Number(entityPrice?.price)) {
          setSentiment("bearish");
        }
      }
    };

    return (
      <div className="flex flex-col gap-[15px] p-[5px]">
        <div
          className="flex items-center gap-[6px] text-xs font-semibold text-shady-gray hover:text-bearish-red cursor-pointer justify-end uppercase tracking-wider transition-colors"
          onClick={() => {
            setMode(null);
            setTargetPrice(null);
            setTargetDate(null);
            setSentiment(null);
          }}
        >
          Remove
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="flex flex-col md:flex-row items-stretch gap-[10px]">
          {/* Current Price Card */}
          <div className="flex flex-col flex-1 p-[12px] bg-inactive-gray/5 border border-solid border-[#ddd] rounded-[12px]">
            <span className="text-[11px] uppercase tracking-wider font-bold text-shady-gray mb-[4px]">
              Current Price
            </span>
            {entity ? (
              entityPrice?.price ? (
                <div className="flex items-baseline gap-[4px]">
                  <span className="text-[20px] font-bold text-subtle-black leading-none">
                    {entityPrice?.price}
                  </span>
                  <span className="text-[12px] font-medium text-shady-gray uppercase">
                    {entityPrice?.currency}
                  </span>
                </div>
              ) : isValidating ? (
                <LoaderSingleLine
                  loadingBackgroundClassName="!h-[20px] md:!h-[20px] w-[100px]"
                  loadingContainerClassName="!h-[20px] md:!h-[20px] w-[100px]"
                />
              ) : (
                <span className="text-[18px] font-medium text-shady-gray">
                  N/A
                </span>
              )
            ) : (
              <span className="text-[14px] font-medium text-shady-gray italic">
                Tag an entity first
              </span>
            )}
          </div>

          <div className="hidden md:flex items-center justify-center text-shady-gray">
            <FiArrowRight size={20} />
          </div>

          <div
            className={twMerge(
              "flex flex-col flex-1 p-[12px] border border-solid rounded-[12px] transition-all duration-300 relative overflow-hidden",
              !entity && "opacity-[0.5] pointer-events-none",
              upside !== null && upside > 0
                ? "border-bullish-green/40 bg-bullish-green/5 shadow-sm"
                : upside !== null && upside < 0
                ? "border-bearish-red/40 bg-bearish-red/5 shadow-sm"
                : "border-[#ddd] bg-white"
            )}
          >
            <div className="flex items-center justify-between mb-[4px]">
              <span className="text-[11px] uppercase tracking-wider font-bold text-shady-gray">
                Target Price
              </span>
              {upside !== null && (
                <span
                  className={twMerge(
                    "text-[11px] font-bold px-[6px] py-[1px] rounded-full",
                    upside > 0
                      ? "bg-bullish-green text-white"
                      : "bg-bearish-red text-white"
                  )}
                >
                  {upside > 0 ? "+" : ""}
                  {upside.toFixed(1)}% {upside > 0 ? "Upside" : "Downside"}
                </span>
              )}
            </div>

            <div className="flex items-center gap-[5px]">
              <input
                className="flex-1 !border-0 !outline-none !shadow-none focus:!outline-none !ring-0 !p-0 text-[20px] font-bold bg-transparent placeholder:text-shady-gray/50"
                type="number"
                placeholder="0.00"
                value={targetPrice || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const currentPrice = entityPrice?.price;

                  if (currentPrice) {
                    if (Number(value) > Number(currentPrice)) {
                      setSentiment("bullish");
                    } else if (Number(value) < Number(currentPrice)) {
                      setSentiment("bearish");
                    }
                  }
                  setTargetPrice(value);
                }}
              />
              <span className="text-[12px] font-medium text-shady-gray uppercase">
                {entityPrice?.currency}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-row items-stretch gap-[10px]">
          <div className="flex-1"></div>
          <div className="hidden md:flex items-center justify-center text-shady-gray">
            <FiArrowRight size={20} className="opacity-0" />
          </div>
          <div className="flex-1">
            {/* Quick Price Adjustments */}
            {entity && entityPrice?.price && (
              <div className="flex flex-wrap items-center gap-[8px]">
                {[-10, -5, +5, +10].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    className={twMerge(
                      "px-[10px] py-[4px] rounded-full text-[12px] font-medium transition-all",
                      "border border-solid border-[#ddd] bg-white hover:bg-smart-teal/10 hover:border-smart-teal/30 text-shady-gray hover:text-smart-teal"
                    )}
                    onClick={() => adjustPrice(pct)}
                  >
                    {pct > 0 ? "+" : ""}
                    {pct}%
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-[10px] mt-[5px] pt-[15px] border-0 border-t border-solid border-[#ddd]">
          <div className="flex items-center gap-[10px]">
            <DatePicker
              removeButton={
                <button className="p-0 outline-none border-0 bg-transparent text-bearish-red hover:text-[#85273a] mt-[5px]">
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="text-[20px]"
                  />
                </button>
              }
              placeholder="Target Date"
              containerClassName="flex flex-row items-center gap-[10px]"
              value={
                targetDate ? new Date(targetDate + "T00:00:00") : undefined
              }
              inputClassName="text-[14px] rounded-[10px]"
              minDate={new Date()}
              onSelect={(date) => {
                if (!date) {
                  setTargetDate(null);
                  return;
                }
                setTargetDate(toLocalDateString(date));
              }}
            />
          </div>

          <div className="flex items-center gap-[6px]">
            {[1, 3, 6, 12].map((m) => {
              const presetDate = new Date();
              presetDate.setMonth(presetDate.getMonth() + m);
              const presetValue = toLocalDateString(presetDate);
              return (
                <button
                  key={m}
                  type="button"
                  className={twMerge(
                    "px-[10px] py-[4px] rounded-full text-[12px] font-medium transition-all",
                    "border border-solid",
                    targetDate === presetValue
                      ? "border-smart-teal bg-smart-teal/10 text-smart-teal"
                      : "border-[#ddd] bg-white hover:bg-smart-teal/10 hover:border-smart-teal/30 text-shady-gray hover:text-smart-teal"
                  )}
                  onClick={() => setTargetDateFromPreset(m)}
                >
                  {m >= 12 ? `${m / 12}Y` : `${m}M`}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }, [
    entity,
    entityPrice?.currency,
    entityPrice?.price,
    isValidating,
    setMode,
    setSentiment,
    setTargetDate,
    setTargetPrice,
    targetDate,
    targetPrice,
  ]);

  const content = useMemo(() => {
    return (
      <div className="border border-solid border-[#ddd] p-[10px] rounded-[10px] mb-[10px]">
        {editor}
        {mode === "prediction" && prediction}
        {mode === "polling" && polling}
      </div>
    );
  }, [editor, mode, polling, prediction]);

  const submitErrorMessage = useMemo(() => {
    if (!taggedEntity) {
      return "Tag an entity to post";
    }
    if (mode === "polling") {
      return "Please enter a question and at least 2 options for the poll with a closing date";
    }
    if (mode === "prediction") {
      return "Please enter both target date and target price for prediction";
    }
    return "Write or attach something to post";
  }, [mode, taggedEntity]);

  const shouldShowPredictionAndPollingOptions = useMemo(() => {
    return !parent && !onlyAttachments && !isEditing;
  }, [isEditing, onlyAttachments, parent]);

  return (
    <div
      {...getRootProps()}
      className={twMerge(
        "flex flex-col mb-[10px] relative",
        isDragOver &&
          "outline-none ring-2 ring-smart-teal ring-dashed rounded-[10px]"
      )}
    >
      <input {...getInputProps()} />
      {isDragOver && (
        <div className="absolute inset-0 z-10 bg-smart-teal/10 rounded-[10px] flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-[8px]">
            <FontAwesomeIcon
              icon={faPaperclip}
              className="text-smart-teal text-[28px]"
            />
            <span className="text-smart-teal font-semibold text-[14px]">
              Drop files to attach
            </span>
          </div>
        </div>
      )}
      <div className="mb-[10px] mt-[-10px]">{suggestions}</div>
      <div className="flex gap-[15px]">
        <div className="hidden md:block">
          <ProfilePicture
            className="!mr-0"
            src={account?.avatarUrl?.small}
            size={mobile ? 33 : 45}
          />
        </div>
        <div className="flex flex-col flex-1">
          <div
            className={twMerge(
              "block md:flex justify-between gap-[15px] items-center mb-[10px]"
            )}
          >
            <div className="mb-[10px] md:mb-0 flex-1 transition-all">
              {commentable}
            </div>
          </div>
          {content}
          <div className="flex flex-col">
            {sentimentInput}

            {/* Add to your post */}
            <div className="mt-[15px]">
              <div className="flex items-center justify-between mb-[10px]">
                <div className="flex items-center gap-[6px]">
                  <span className="text-[13px] font-bold text-subtle-black">
                    Add to your post
                  </span>
                  {!mode && (
                    <span className="text-[13px] text-shady-gray">
                      · optional
                    </span>
                  )}
                </div>
                {mode && (
                  <button
                    className={twMerge(
                      "flex items-center gap-[5px] px-[15px] py-[6px] rounded-full text-[12px] font-semibold transition-all",
                      "border border-solid border-smart-teal bg-smart-teal/10 text-smart-teal hover:bg-smart-teal/20"
                    )}
                    onClick={() => {
                      setIsAddToPostExpanded(!isAddToPostExpanded);
                      setMode(null);
                    }}
                  >
                    {mode === "polling" ? "Poll" : "Prediction"}
                    {isAddToPostExpanded ? (
                      <FiChevronUp size={15} />
                    ) : (
                      <FiX size={15} className="text-smart-teal" />
                    )}
                  </button>
                )}
              </div>
              {isAddToPostExpanded && (
                <div className="flex flex-col md:flex-row gap-[8px]">
                  {attachmentsInput}
                  {shouldShowPredictionAndPollingOptions && (
                    <>
                      <button
                        className={twMerge(
                          "relative flex-1 flex flex-col gap-[4px] p-[12px] text-left bg-white",
                          "border border-solid rounded-[10px] transition-all",
                          mode === "polling"
                            ? "border-smart-teal bg-smart-teal/5"
                            : mode
                            ? "border-[#ddd] opacity-40 cursor-not-allowed"
                            : "border-[#ddd] cursor-pointer hover:border-smart-teal/40 hover:bg-smart-teal/5"
                        )}
                        disabled={!!mode && mode !== "polling"}
                        onClick={() => {
                          if (attachments.length) {
                            setAttachmentRemovalConfirm("polling");
                            return;
                          }

                          if (mode === "polling") {
                            setMode(null);
                          } else {
                            setMode("polling");
                            setPollOptions(["", ""]);
                          }
                        }}
                      >
                        <div className="flex items-center gap-[10px]">
                          <div className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] bg-smart-teal/10 flex-shrink-0">
                            <FiBarChart2
                              size={14}
                              className="text-smart-teal"
                            />
                          </div>
                          <div className="flex flex-col gap-[2px]">
                            <span className="font-semibold text-[14px] !text-subtle-black">
                              Poll
                            </span>
                            <span className="text-[12px] text-shady-gray">
                              Ask the network
                            </span>
                          </div>
                        </div>
                      </button>
                      <button
                        className={twMerge(
                          "relative flex-1 flex flex-col gap-[4px] p-[12px] text-left bg-white",
                          "border border-solid rounded-[10px] transition-all",
                          mode === "prediction"
                            ? "border-smart-teal bg-smart-teal/5"
                            : mode
                            ? "border-[#ddd] opacity-40 cursor-not-allowed"
                            : "border-[#ddd] cursor-pointer hover:border-smart-teal/40 hover:bg-smart-teal/5"
                        )}
                        disabled={!!mode && mode !== "prediction"}
                        onClick={() => {
                          if (attachments.length) {
                            setAttachmentRemovalConfirm("prediction");
                            return;
                          }

                          setMode(mode === "prediction" ? null : "prediction");
                        }}
                      >
                        <div className="flex items-center gap-[10px]">
                          <div className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] bg-smart-teal/10 flex-shrink-0">
                            <FiTarget size={14} className="text-smart-teal" />
                          </div>
                          <div className="flex flex-col gap-[2px]">
                            <span className="font-semibold text-[14px] !text-subtle-black">
                              Prediction
                            </span>
                            <span className="text-[12px] text-shady-gray">
                              Track an outcome
                            </span>
                          </div>
                        </div>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {attachments.length ? (
              <div className="flex flex-wrap gap-[10px] mt-[15px]">
                {attachmentsPreview}
              </div>
            ) : null}

            {/* Bottom bar */}
            <div className="flex items-center justify-between mt-[15px] pt-[12px] border-0 border-t border-solid border-[#eee]">
              <div className="flex items-center gap-[8px]">
                {account?.canPostCommentAnonymously && (
                  <>
                    <SwitchButton
                      checked={isAnonymous}
                      onClick={toggleAnonymous}
                    />
                    <span className="text-[13px] font-medium">
                      Post anonymously
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-[12px]">
                {onCancel && (
                  <button
                    className={twMerge(
                      "border border-solid border-bearish-red min-h-[36px]",
                      "bg-white hover:bg-bearish-red",
                      "text-bearish-red hover:text-white",
                      "font-bold rounded-full px-[15px] py-[5px]"
                    )}
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                )}
                {!isEditing && (
                  <span className="hidden md:block text-[13px] text-shady-gray">
                    Draft saved
                  </span>
                )}
                <OverlayTrigger
                  overlay={
                    canSubmit ? (
                      <></>
                    ) : (
                      <Tooltip className="hidden md:block">
                        {submitErrorMessage}
                      </Tooltip>
                    )
                  }
                >
                  <button
                    className={twMerge(
                      "flex items-center justify-center gap-[5px] cursor-pointer min-h-[36px]",
                      "rounded-full px-[20px] py-[5px] font-bold text-[14px]",
                      "border border-solid border-nearly-white",
                      canSubmit
                        ? "bg-smart-teal hover:bg-dark-teal text-white"
                        : "bg-[#f0f0f0] text-[#bbb]"
                    )}
                    disabled={!canSubmit || isSubmitting}
                    onClick={submit}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                      />
                    ) : null}
                    Post
                  </button>
                </OverlayTrigger>
              </div>
            </div>
          </div>
        </div>
      </div>
      {attachmentRemovalConfirm && (
        <ConfirmModal
          title="Attachments will be removed"
          confirmButtonText="Okay"
          onConfirm={() => {
            setAttachments([]);
            if (attachmentRemovalConfirm === "polling") {
              if (mode === "polling") {
                setMode(null);
              } else {
                setMode("polling");
                setPollOptions(["", ""]);
              }
            } else {
              setMode(mode === "prediction" ? null : "prediction");
            }
            setAttachmentRemovalConfirm(null);
          }}
          onClose={() => setAttachmentRemovalConfirm(null)}
        >
          <p className="text-[14px] text-shady-gray mt-[10px]">
            {attachmentRemovalConfirm === "polling"
              ? "Attachments will be removed when creating a poll."
              : "Attachments will be removed when creating a prediction."}
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}
